import React from 'react';
import { joinClassNames } from '../../utils';
import { ProcessedSection } from '../../sectionProcessing';
import sectionStyles from '../../StructuredSections.module.scss';
import { SitePageHtmlContent } from '../SitePageHtmlContent';

const EMPTY_TEXT_FALLBACK = '';

function toCellHtml(value: unknown): string {
    if (value === null || value === undefined) {
        return EMPTY_TEXT_FALLBACK;
    }
    return value.toString();
}

function stripHtml(value: string): string {
    return value.replace(/<[^>]*>/g, '').trim();
}

export function renderTableBlockContent(section: ProcessedSection): React.ReactNode {
    const block = section.tableBlock;
    if (!block || block.rows.length === 0) {
        return null;
    }

    const trimmedCaption = block.caption?.trim() ?? null;
    const hasCaption = Boolean(trimmedCaption);
    const firstRow = block.rows[0]!;
    const headerRow = block.firstRowIsHeader ? firstRow : null;
    const dataRows = block.firstRowIsHeader ? block.rows.slice(1) : block.rows;
    const columnCount = firstRow.length;
    const hasHeaderRow = Boolean(headerRow);
    const hasRowHeaders = block.firstColumnIsHeader;

    if (columnCount === 0) {
        return null;
    }

    const wrapperClassName = joinClassNames(
        sectionStyles.tableWrapper,
        !hasCaption ? sectionStyles.tableWrapperNoCaption : undefined
    );

    const headerColumnIndices = hasHeaderRow
        ? Array.from({ length: columnCount }, (_, columnIndex) => columnIndex)
              .filter((columnIndex) => !(hasRowHeaders && columnIndex === 0))
              .filter((columnIndex) => {
                  const columnHeaderHtml = headerRow ? toCellHtml(headerRow[columnIndex]) : '';
                  const normalizedHeader = stripHtml(columnHeaderHtml);
                  const hasCellContent = dataRows.some((row) => {
                      const cellValue = toCellHtml(row[columnIndex]);
                      return stripHtml(cellValue).length > 0;
                  });
                  return normalizedHeader.length > 0 || hasCellContent;
              })
        : [];

    const mobileColumns =
        headerColumnIndices.length > 0
            ? headerColumnIndices.map((columnIndex) => {
                  const columnHeader = headerRow ? toCellHtml(headerRow[columnIndex]) : '';
                  const columnCells = dataRows.map((row) => {
                      const cellValue = toCellHtml(row[columnIndex]);
                      const rowHeaderValue =
                          hasRowHeaders && columnIndex !== 0 ? toCellHtml(row[0]) : null;
                      return {
                          value: cellValue,
                          rowHeader: rowHeaderValue,
                      };
                  });

                  return {
                      columnHeader,
                      columnCells,
                  };
              })
            : null;

    const mobileRows =
        !hasHeaderRow && hasRowHeaders
            ? dataRows
                  .map((row) => {
                      const rowHeader = toCellHtml(row[0]);
                      const cells = row.slice(1).map((cell) => toCellHtml(cell));
                      const hasVisibleContent =
                          stripHtml(rowHeader).length > 0 ||
                          cells.some((cell) => stripHtml(cell).length > 0);
                      if (!hasVisibleContent) {
                          return null;
                      }
                      return {
                          rowHeader,
                          cells,
                      };
                  })
                  .filter((row): row is { rowHeader: string; cells: string[] } => row !== null)
            : null;

    const mobileFallbackRows =
        !mobileColumns && !mobileRows
            ? dataRows
                  .map((row, rowIndex) => {
                      const cells = row.map((cell) => toCellHtml(cell));
                      const hasVisibleContent = cells.some(
                          (cellValue) => stripHtml(cellValue).length > 0
                      );
                      if (!hasVisibleContent) {
                          return null;
                      }
                      return {
                          index: rowIndex,
                          cells,
                      };
                  })
                  .filter((row): row is { index: number; cells: string[] } => row !== null)
            : null;

    return (
        <div className={wrapperClassName}>
            {hasCaption ? (
                <div className={sectionStyles.tableCaptionMobile}>{trimmedCaption}</div>
            ) : null}
            <table className={sectionStyles.table}>
                {hasCaption ? <caption>{trimmedCaption}</caption> : null}
                {headerRow ? (
                    <thead>
                        <tr>
                            {headerRow.map((cell, columnIndex) => (
                                <th key={`header-${columnIndex}`} scope="col">
                                    <SitePageHtmlContent html={toCellHtml(cell)} as="span" />
                                </th>
                            ))}
                        </tr>
                    </thead>
                ) : null}
                <tbody>
                    {dataRows.map((row, rowIndex) => (
                        <tr key={`row-${rowIndex}`}>
                            {Array.from({ length: columnCount }, (_, columnIndex) => {
                                const cell = row[columnIndex];
                                const cellHtml = toCellHtml(cell);
                                const isRowHeader = hasRowHeaders && columnIndex === 0;
                                if (isRowHeader) {
                                    return (
                                        <th key={`cell-${rowIndex}-${columnIndex}`} scope="row">
                                            <SitePageHtmlContent html={cellHtml} as="span" />
                                        </th>
                                    );
                                }

                                const columnHeaderHtml =
                                    headerRow && headerRow[columnIndex]
                                        ? toCellHtml(headerRow[columnIndex])
                                        : '';
                                const columnLabel = stripHtml(columnHeaderHtml);
                                const rowHeaderHtml =
                                    hasRowHeaders && columnIndex !== 0 ? toCellHtml(row[0]) : '';
                                const rowHeaderValue = stripHtml(rowHeaderHtml);

                                return (
                                    <td
                                        key={`cell-${rowIndex}-${columnIndex}`}
                                        data-column-label={columnLabel}
                                        data-row-header={rowHeaderValue}
                                    >
                                        <SitePageHtmlContent html={cellHtml} as="div" />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            {mobileColumns ? (
                <div className={sectionStyles.tableMobile}>
                    {mobileColumns.map((column, columnIndex) => (
                        <div
                            key={`column-${columnIndex}`}
                            className={sectionStyles.tableMobileColumn}
                        >
                            {stripHtml(column.columnHeader).length > 0 ? (
                                <div className={sectionStyles.tableMobileColumnHeader}>
                                    <SitePageHtmlContent html={column.columnHeader} as="div" />
                                </div>
                            ) : null}
                            {column.columnCells.map((cell, rowIndex) => {
                                const rowHeaderValue =
                                    hasRowHeaders && cell.rowHeader ? cell.rowHeader : '';
                                const cellValue = cell.value;
                                const hasRowHeaderContent = stripHtml(rowHeaderValue).length > 0;
                                const hasCellContent = stripHtml(cellValue).length > 0;
                                if (!hasCellContent && !hasRowHeaderContent) {
                                    return null;
                                }

                                return (
                                    <div
                                        key={`column-${columnIndex}-row-${rowIndex}`}
                                        className={sectionStyles.tableMobileCell}
                                    >
                                        {hasRowHeaderContent ? (
                                            <div className={sectionStyles.tableMobileRowHeader}>
                                                <SitePageHtmlContent
                                                    html={toCellHtml(cell.rowHeader)}
                                                    as="div"
                                                />
                                            </div>
                                        ) : null}
                                        <div className={sectionStyles.tableMobileCellContent}>
                                            <SitePageHtmlContent html={cell.value} as="div" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : null}
            {mobileRows ? (
                <div className={sectionStyles.tableMobile}>
                    {mobileRows.map((row, rowIndex) => (
                        <div key={`row-${rowIndex}`} className={sectionStyles.tableMobileColumn}>
                            {stripHtml(row.rowHeader).length > 0 ? (
                                <div className={sectionStyles.tableMobileColumnHeader}>
                                    <SitePageHtmlContent html={row.rowHeader} as="div" />
                                </div>
                            ) : null}
                            {row.cells.map((cell, cellIndex) => {
                                const rawValue = toCellHtml(cell);
                                if (stripHtml(rawValue).length === 0) {
                                    return null;
                                }
                                return (
                                    <div
                                        key={`row-${rowIndex}-cell-${cellIndex}`}
                                        className={sectionStyles.tableMobileCell}
                                    >
                                        <div className={sectionStyles.tableMobileCellContent}>
                                            <SitePageHtmlContent html={rawValue} as="div" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : null}
            {mobileFallbackRows ? (
                <div className={sectionStyles.tableMobile}>
                    {mobileFallbackRows.map((row) => (
                        <div
                            key={`fallback-row-${row.index}`}
                            className={sectionStyles.tableMobileColumn}
                        >
                            <div className={sectionStyles.tableMobileColumnHeader}>
                                {`Row ${row.index + 1}`}
                            </div>
                            {row.cells.map((cell, cellIndex) => {
                                const value = cell.trim();
                                if (stripHtml(value).length === 0) {
                                    return null;
                                }
                                return (
                                    <div
                                        key={`fallback-row-${row.index}-cell-${cellIndex}`}
                                        className={sectionStyles.tableMobileCell}
                                    >
                                        <div className={sectionStyles.tableMobileCellContent}>
                                            <SitePageHtmlContent html={cell} as="div" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
